// Yeni Kullanıcı — plan §4.7.2 + design ref #screen-user-new.
// Radio card rol seçimi (Admin / Danışman / Yazar) + ad + email + "Davet Gönder".

import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { InviteUserResponse, UsersListResponse } from "@mahir/shared";
import { api, ApiError } from "../../api/client";
import { ROLE_UI_COPY, assignableRoles } from "../../lib/role-ui";
import { useAuthStore } from "../../stores/auth-store";

interface RoleCard {
  name: "superadmin" | "admin" | "consultant" | "writer";
  title: string;
  description: string;
  accent: string;
}

const ROLE_META: Record<RoleCard["name"], RoleCard> = {
  // Finar rev. madde 12 — yalnız Sistem Yöneticisi bu kartı görür (assignableRoles)
  superadmin: {
    name: "superadmin",
    ...ROLE_UI_COPY.superadminCard,
    accent: "var(--color-error)",
  },
  admin: {
    name: "admin",
    title: "Admin",
    description: "Sistem ayarları, çerçeveler, tüm kullanıcı ve müşteri erişimi.",
    accent: "var(--color-error)",
  },
  consultant: {
    name: "consultant",
    title: "Danışman",
    description: "Kendi müşterilerini yönetir, proje oluşturur ve form doldurur.",
    accent: "var(--color-primary)",
  },
  writer: {
    name: "writer",
    title: "Yazar",
    description: "Atanan projelerde form doldurur, AI üretimini gözden geçirir.",
    accent: "var(--color-success)",
  },
};

export function UserNew(): JSX.Element {
  const navigate = useNavigate();
  const actor = useAuthStore((s) => s.user);
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [roleId, setRoleId] = useState("");
  const [roles, setRoles] = useState<UsersListResponse["roles"]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invited, setInvited] = useState<InviteUserResponse | null>(null);

  useEffect(() => {
    void api<UsersListResponse>("/admin/users").then((res) => {
      setRoles(res.roles);
      // Default: consultant
      const c = res.roles.find((r) => r.name.toLowerCase() === "consultant");
      if (c) setRoleId(c.id);
    });
  }, []);

  const canSubmit = email.trim() && fullName.trim().length >= 2 && roleId && !submitting;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api<InviteUserResponse>("/admin/users", {
        method: "POST",
        body: { email: email.trim(), fullName: fullName.trim(), roleId },
      });
      setInvited(res);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError("Bu email zaten kayıtlı.");
      } else {
        setError(err instanceof Error ? err.message : "Davet gönderilemedi");
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (invited) {
    return (
      <div className="p-8 mx-auto" style={{ fontFamily: "var(--font-sans)", maxWidth: 620 }}>
        <div
          className="rounded-xl p-6"
          style={{
            background: "var(--color-surface)",
            border: "1px solid var(--color-border)",
          }}
        >
          <div
            className="flex items-center justify-center rounded-full mb-4"
            style={{
              width: 56,
              height: 56,
              background: "color-mix(in srgb, var(--color-success) 15%, white)",
              color: "var(--color-success)",
              fontSize: "1.8rem",
            }}
            aria-hidden="true"
          >
            ✓
          </div>
          <h1
            className="text-2xl mb-2"
            style={{ fontFamily: "var(--font-display)", color: "var(--color-text)" }}
          >
            Davet Gönderildi
          </h1>
          {/* #011: production-ready dinamik mesaj — emailSent flag'ine göre. */}
          {invited.emailSent ? (
            <p className="text-sm" style={{ color: "var(--color-text-2)" }}>
              <strong>{invited.user.fullName}</strong> ({invited.user.email}) davet edildi.
              Aktivasyon e-postası kullanıcının gelen kutusuna gönderildi.
            </p>
          ) : (
            <>
              <p className="text-sm" style={{ color: "var(--color-text-2)" }}>
                <strong>{invited.user.fullName}</strong> ({invited.user.email}) davet edildi.
                Aktivasyon e-postası gönderilemedi (SMTP konfigürasyonu eksik veya başarısız).
                Aşağıdaki aktivasyon bağlantısını kullanıcıya manuel iletmeniz gerekiyor:
              </p>
              {invited.activationUrl && (
                <div
                  className="mt-3 px-3 py-2 rounded text-xs"
                  style={{
                    background: "var(--color-bg)",
                    border: "1px solid var(--color-border)",
                    fontFamily: "var(--font-mono)",
                    wordBreak: "break-all",
                  }}
                >
                  <button
                    onClick={() => {
                      void navigator.clipboard.writeText(invited.activationUrl ?? "");
                    }}
                    className="float-right text-xs px-2 py-0.5 rounded ml-2"
                    style={{
                      background: "var(--color-primary)",
                      color: "var(--color-surface)",
                    }}
                    title="Panoya kopyala"
                  >
                    Kopyala
                  </button>
                  {invited.activationUrl}
                </div>
              )}
            </>
          )}
          <div className="mt-6 flex gap-2">
            <button
              onClick={() => void navigate("/admin/users")}
              className="px-4 py-2 rounded-lg font-semibold text-sm"
              style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
            >
              Kullanıcı listesine dön
            </button>
            <button
              onClick={() => {
                setInvited(null);
                setEmail("");
                setFullName("");
              }}
              className="px-4 py-2 rounded-lg text-sm"
              style={{
                background: "transparent",
                border: "1px solid var(--color-border)",
                color: "var(--color-text)",
              }}
            >
              Bir tane daha davet et
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 mx-auto" style={{ fontFamily: "var(--font-sans)", maxWidth: 720 }}>
      <Link to="/admin/users" className="text-sm" style={{ color: "var(--color-primary)" }}>
        ← Kullanıcı listesine dön
      </Link>
      <h1
        className="mt-4 text-3xl"
        style={{ fontFamily: "var(--font-display)", color: "var(--color-text)" }}
      >
        Yeni Kullanıcı Davet Et
      </h1>
      <p className="text-sm mt-1 mb-6" style={{ color: "var(--color-text-2)" }}>
        Kullanıcı rolünü seçin, bilgileri girin. Davet e-postasıyla aktivasyon linki gönderilecek.
      </p>

      <form onSubmit={(e) => void onSubmit(e)} className="grid gap-5">
        <div>
          <label className="block mb-2 text-sm font-medium" style={{ color: "var(--color-text)" }}>
            Rol
          </label>
          <div
            className="grid gap-3"
            style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}
          >
            {assignableRoles(roles, actor).map((r) => {
              const meta = ROLE_META[r.name.toLowerCase() as RoleCard["name"]] ?? {
                name: r.name,
                title: r.name,
                description: "Özel rol",
                accent: "var(--color-text-2)",
              };
              const active = r.id === roleId;
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setRoleId(r.id)}
                  className="text-left rounded-xl p-4"
                  style={{
                    background: active ? "rgba(15,42,61,0.04)" : "var(--color-surface)",
                    border: active ? `2px solid ${meta.accent}` : "1px solid var(--color-border)",
                    cursor: "pointer",
                  }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      style={{
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        border: `2px solid ${meta.accent}`,
                        background: active ? meta.accent : "transparent",
                        flexShrink: 0,
                      }}
                    />
                    <div
                      style={{
                        fontFamily: "var(--font-display)",
                        color: meta.accent,
                        fontSize: "1.05rem",
                        fontWeight: 600,
                      }}
                    >
                      {meta.title}
                    </div>
                  </div>
                  <p
                    style={{
                      fontSize: "0.8rem",
                      color: "var(--color-text-2)",
                      lineHeight: 1.5,
                    }}
                  >
                    {meta.description}
                  </p>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <label className="block">
            <span
              className="block mb-1.5 text-sm font-medium"
              style={{ color: "var(--color-text)" }}
            >
              Ad Soyad
            </span>
            <input
              type="text"
              required
              minLength={2}
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="ör. Ayşe Kaya"
              className="w-full px-3 py-2.5 rounded-lg border"
              style={{
                background: "var(--color-surface)",
                borderColor: "var(--color-border)",
                fontSize: "0.9rem",
              }}
            />
          </label>
          <label className="block">
            <span
              className="block mb-1.5 text-sm font-medium"
              style={{ color: "var(--color-text)" }}
            >
              E-posta
            </span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ayse@firma.com"
              className="w-full px-3 py-2.5 rounded-lg border"
              style={{
                background: "var(--color-surface)",
                borderColor: "var(--color-border)",
                fontSize: "0.9rem",
              }}
            />
          </label>
        </div>

        {error ? (
          <div
            role="alert"
            className="px-3 py-2 rounded-lg text-sm"
            style={{
              background: "color-mix(in srgb, var(--color-error) 10%, white)",
              color: "var(--color-error)",
            }}
          >
            {error}
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => void navigate("/admin/users")}
            className="px-3 py-2 rounded-lg text-sm"
            style={{
              background: "transparent",
              border: "1px solid var(--color-border)",
              color: "var(--color-text-3)",
            }}
          >
            İptal
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="px-4 py-2 rounded-lg font-semibold text-sm disabled:opacity-50"
            style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
          >
            {submitting ? "Gönderiliyor…" : "Davet Gönder"}
          </button>
        </div>
      </form>
    </div>
  );
}
